import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, DollarSign, CheckCircle2, XCircle, ChefHat, Truck, LogOut, ShoppingBag } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, kioskService } from '../../services/api';
import { toast } from 'sonner';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [myKiosks, setMyKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [ordersData, kiosksData] = await Promise.all([
        orderService.list(),
        kioskService.getMyKiosks(),
      ]);
      setOrders(ordersData);
      setMyKiosks(kiosksData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await orderService.updateStatus(orderId, status);
      toast.success('Status atualizado!');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filterOrders = () => {
    if (activeTab === 'pending') {
      return orders.filter((o) => ['created', 'payment_pending', 'payment_approved', 'received'].includes(o.status));
    }
    if (activeTab === 'preparing') {
      return orders.filter((o) => ['accepted', 'preparing'].includes(o.status));
    }
    if (activeTab === 'delivery') {
      return orders.filter((o) => o.status === 'out_for_delivery');
    }
    if (activeTab === 'completed') {
      return orders.filter((o) => ['delivered', 'cancelled'].includes(o.status));
    }
    return orders;
  };

  const filteredOrders = filterOrders();
  const todayOrders = orders.filter((o) => {
    const today = new Date().toDateString();
    return new Date(o.created_at).toDateString() === today;
  });
  const todayRevenue = todayOrders
    .filter((o) => o.status === 'delivered')
    .reduce((sum, o) => sum + o.total, 0);

  if (loading) return <LoadingSpinner size="lg" text="Carregando..." />;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="partner-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="font-['Outfit'] text-lg font-bold">Painel do Quiosque</h1>
            <p className="text-xs text-muted-foreground">
              {myKiosks[0]?.display_name || user?.full_name}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/')}
              className="px-3 py-1.5 bg-muted rounded-full text-sm font-semibold hover:bg-muted/80 transition-colors"
            >
              App
            </button>
            <button
              data-testid="partner-logout"
              onClick={handleLogout}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-border p-3">
            <Package className="w-5 h-5 text-primary mb-1" />
            <p className="text-xs text-muted-foreground">Pedidos hoje</p>
            <p className="font-bold text-lg">{todayOrders.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-3">
            <DollarSign className="w-5 h-5 text-secondary mb-1" />
            <p className="text-xs text-muted-foreground">Faturado hoje</p>
            <p className="font-bold text-lg">R$ {todayRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border border-border p-3">
            <Clock className="w-5 h-5 text-accent mb-1" />
            <p className="text-xs text-muted-foreground">Em aberto</p>
            <p className="font-bold text-lg">
              {orders.filter((o) => !['delivered', 'cancelled', 'expired'].includes(o.status)).length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { key: 'pending', label: 'Novos' },
            { key: 'preparing', label: 'Em preparo' },
            { key: 'delivery', label: 'Entregando' },
            { key: 'completed', label: 'Concluídos' },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white border border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Orders */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">Nenhum pedido nessa categoria</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                data-testid={`partner-order-${order.id}`}
                className="bg-white rounded-xl border border-border p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-foreground">#{order.order_number?.slice(-6)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.created_at).toLocaleTimeString('pt-BR')}
                    </p>
                  </div>
                  <span className="font-bold text-primary">R$ {order.total.toFixed(2)}</span>
                </div>

                <div className="bg-muted/50 rounded-lg p-3 mb-3 space-y-1">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span className="font-semibold">R$ {item.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                {order.delivery_location.reference && (
                  <div className="text-sm bg-secondary/10 rounded-lg p-2 mb-3">
                    <p><strong>📍 Local:</strong> {order.delivery_location.reference}</p>
                    {order.delivery_location.umbrella_color && (
                      <p><strong>🏖️ Cor:</strong> {order.delivery_location.umbrella_color}</p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  {['payment_approved', 'received'].includes(order.status) && (
                    <>
                      <button
                        data-testid={`accept-${order.id}`}
                        onClick={() => updateStatus(order.id, 'accepted')}
                        className="flex-1 py-2 bg-secondary text-secondary-foreground rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Aceitar
                      </button>
                      <button
                        onClick={() => updateStatus(order.id, 'cancelled')}
                        className="py-2 px-3 bg-destructive/10 text-destructive rounded-lg font-semibold text-sm"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {order.status === 'accepted' && (
                    <button
                      data-testid={`prepare-${order.id}`}
                      onClick={() => updateStatus(order.id, 'preparing')}
                      className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
                    >
                      <ChefHat className="w-4 h-4" />
                      Iniciar preparo
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      data-testid={`deliver-${order.id}`}
                      onClick={() => updateStatus(order.id, 'out_for_delivery')}
                      className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
                    >
                      <Truck className="w-4 h-4" />
                      Saiu para entrega
                    </button>
                  )}
                  {order.status === 'out_for_delivery' && (
                    <button
                      data-testid={`delivered-${order.id}`}
                      onClick={() => updateStatus(order.id, 'delivered')}
                      className="flex-1 py-2 bg-secondary text-secondary-foreground rounded-lg font-semibold text-sm flex items-center justify-center gap-1"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Marcar entregue
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
